import { useEffect } from 'react';
import useMountEffect from 'hooks/useMountEffect';
import { VersionChange } from 'types/enums';
import { usePurchaseControllerContext } from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import { useModalContext } from 'contexts/ModalContext/ModalContext';
import { useUserCreditsContext } from 'contexts/UserCreditsContext/UserCreditsContext';
import { useAuthentication } from 'components/AuthenticationProvider/AuthenticationProvider';
import { CustomEvents } from 'types/enums/customEvents';

export enum PluginTransportStates {
    'PLAYING' = 'playing',
    'PAUSED' = 'paused',
    'STOPPED' = 'stopped',
}

export type PluginTransportStateChangedEventType = {
    sampleId: string;
    oldState: PluginTransportStates;
    newState: PluginTransportStates;
};

export type ShowPricingModalEventType = { creditsRequired: number };

export type SamplePurchaseEventType = {
    sampleId: string;
    creditsSpent: number;
};

export type ClientUpdateRequiredEvent = {
    versionChange: VersionChange;
};

export type PurchaseFlowIsLoadingEvent = {
    guid: string;
    isLoading: boolean;
};

export const useCustomEventController = ({
    stopActivePlayer,
    setUpdateRequired,
}: {
    stopActivePlayer: () => void;
    setUpdateRequired: (versionChange: VersionChange) => void;
}): void => {
    const { showNotEnoughCreditsWarning, showPoorStandingOrSuspended } =
        usePurchaseControllerContext();
    const { getUserCredits } = useUserCreditsContext();
    const { openModal } = useModalContext();
    const { logout } = useAuthentication();

    useMountEffect(() => {
        const registerEvents = () => {
            window.addEventListener(CustomEvents.SAMPLES_PURCHASE, () => {
                getUserCredits();
            });

            window.addEventListener(
                CustomEvents.TRANSPORT_STATE_CHANGED,
                (e) => {
                    const { newState } = (
                        e as CustomEvent<PluginTransportStateChangedEventType>
                    ).detail;

                    if (newState === PluginTransportStates.PLAYING) {
                        stopActivePlayer();
                    }
                },
            );

            window.addEventListener(
                CustomEvents.CLIENT_UPDATE_REQUIRED,
                (e) => {
                    const { versionChange } = (
                        e as CustomEvent<ClientUpdateRequiredEvent>
                    ).detail;

                    setUpdateRequired(versionChange);
                },
            );
        };

        registerEvents();
    });

    useEffect(() => {
        const showPricingModalCallback = (e: Event) => {
            const { creditsRequired } = (
                e as CustomEvent<ShowPricingModalEventType>
            ).detail;

            showNotEnoughCreditsWarning({
                purchaseCostInCredits: creditsRequired,
            });
        };

        window.addEventListener(
            CustomEvents.SHOW_PRICING_MODAL,
            showPricingModalCallback,
        );

        return () => {
            window.removeEventListener(
                CustomEvents.SHOW_PRICING_MODAL,
                showPricingModalCallback,
            );
        };
    }, [openModal, showNotEnoughCreditsWarning]);

    useEffect(() => {
        const showPoorStandingOrSuspendedCallback = () => {
            showPoorStandingOrSuspended();
        };

        window.addEventListener(
            CustomEvents.SHOW_POOR_STANDING_OR_SUSPENDED_MODAL,
            showPoorStandingOrSuspendedCallback,
        );

        return () => {
            window.removeEventListener(
                CustomEvents.SHOW_POOR_STANDING_OR_SUSPENDED_MODAL,
                showPoorStandingOrSuspendedCallback,
            );
        };
    }, [showPoorStandingOrSuspended]);

    useEffect(() => {
        window.addEventListener(CustomEvents.LOGOUT, logout);

        return () => {
            window.removeEventListener(CustomEvents.LOGOUT, logout);
        };
    }, [logout]);
};

export default useCustomEventController;
